/**
 * Where a building can go and where a settler could found a city, judged
 * from what the player knows (fog-safe), for the placement tint, the click
 * check and its refusal (PlacementOverlay draws the tint).
 */
import { axialHexDistance } from '../hexmap/coords';
import { getHexTileMapKey } from '../hexmap/model/keys';
import { isPassable } from '../hexmap/model/map';
import type { HexTileMapKey } from '../hexmap/model/types';
import type { RuleSet } from '../rules/schema';
import {
  type Building,
  type BuildingType,
  type Cell,
  canPlace,
  computeTerritory,
  type EntityId,
  isCity,
  type PlaceRejection,
  type PlayerId,
  type SimContext,
  type SimState,
  territoryOwners,
  type UnitType,
} from '../sim';

/** Tiles in one city's territory (building from a city places there). */
export function cityTiles(state: SimState, rules: RuleSet, cityId: EntityId): Set<HexTileMapKey> {
  const result = new Set<HexTileMapKey>();
  for (const [key, owner] of territoryOwners(state, rules)) {
    if (owner === cityId) result.add(key);
  }
  return result;
}

/** Why a placement click is refused: a `canPlace` reason, or a tile not yet explored. */
export type PlacementRefusal = PlaceRejection | 'unexplored';

/**
 * Whether a placement click on `cell` (building from the city whose tiles are
 * `within`) goes through, else the reason for that tile, in the order a
 * player would fix it: unexplored, the wrong tile type, outside this city's
 * territory, then `canPlace`'s occupied / one per city / cost. Fog-safe:
 * `occupied` is the player's view, and unexplored terrain isn't looked at.
 */
export function placementCheck(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  type: BuildingType,
  cell: Cell,
  within: ReadonlySet<HexTileMapKey>,
  explored: boolean,
  occupied: (key: HexTileMapKey) => boolean
): { ok: true } | { ok: false; reason: PlacementRefusal } {
  if (!explored) return { ok: false, reason: 'unexplored' };
  const check = canPlace(state, ctx, playerId, type, cell, occupied);
  if (!check.ok && (check.reason === 'unknown' || check.reason === 'tile-type')) return check;
  if (!within.has(getHexTileMapKey(cell))) return { ok: false, reason: 'territory' };
  return check;
}

/**
 * Tiles where `playerId` can place a `type` site now. `occupied` is the
 * player's fog-safe view of which tiles hold a building. Placement is always
 * `within: territory`, so only territory tiles are candidates, further limited
 * to `within` when given. Tiles failing `explored` are skipped so the tint
 * never reveals fogged terrain.
 */
export function placeableTiles(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  type: BuildingType,
  occupied: (key: HexTileMapKey) => boolean,
  explored: (key: HexTileMapKey) => boolean,
  within?: ReadonlySet<HexTileMapKey>
): Set<HexTileMapKey> {
  const result = new Set<HexTileMapKey>();
  for (const key of computeTerritory(state, ctx.rules, playerId)) {
    if (within && !within.has(key)) continue;
    if (!explored(key)) continue;
    const tile = ctx.terrain.get(key);
    if (!tile) continue;
    if (canPlace(state, ctx, playerId, type, tile.coordinates, occupied).ok) result.add(key);
  }
  return result;
}

/**
 * Explored tiles where a settler (`unitType`) could found a city, judged only
 * from what the player knows (`knownBuilding`: own buildings, rivals' as last
 * seen), so the tint never reveals a hidden city: the tile is walkable for it,
 * holds no known building, and no known city (sites included) is closer than
 * the city's `minDistanceToCity`. The sim has the final say.
 */
export function foundableTiles(
  ctx: SimContext,
  unitType: UnitType,
  explored: Iterable<HexTileMapKey>,
  knownBuilding: (key: HexTileMapKey) => Building | null
): Set<HexTileMapKey> {
  const result = new Set<HexTileMapKey>();
  const def = ctx.rules.buildings.city;
  const terrain = ctx.rules.units[unitType]?.terrain ?? [];
  if (!def) return result;
  const minDist = def.minDistanceToCity;
  const keys = [...explored];
  const cities = new Map<string, Building>();
  for (const key of keys) {
    const b = knownBuilding(key);
    if (b && isCity(b, ctx.rules)) cities.set(b.id, b);
  }
  for (const key of keys) {
    const tile = ctx.terrain.get(key);
    if (!tile || !isPassable(tile, terrain) || knownBuilding(key)) continue;
    let clear = true;
    for (const city of cities.values()) {
      if (axialHexDistance(tile.coordinates, city.pos) < minDist) {
        clear = false;
        break;
      }
    }
    if (clear) result.add(key);
  }
  return result;
}
